import { Session, SessionInput, UpdateSessionPayload, CreateSessionFromContextPayload, ImportSessionResponse } from '@/types/api';
import { getApiBaseUrl, handleResponse } from './client';

export async function createSession(sessionData?: {
  project_id?: string | null;
  session_title?: string | null;
  system_prompt_id?: string | null;
  default_initial_context_template_id?: string | null;
  default_followup_context_template_id?: string | null;
  sub_agent_id?: string | null;
  parent_session_id?: string | null;
  model_id?: string | null;
  reasoning_effort?: string | null;
}): Promise<Session> {
  const response = await fetch(`${getApiBaseUrl()}/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sessionData || {}),
  });
  return handleResponse<Session>(response);
}

export async function setActiveSession(sessionId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}/set-active`, {
      method: 'POST',
  });
  return handleResponse<{ success: boolean }>(response);
}

export async function stopStreaming(sessionId: string): Promise<{ success: boolean; aborted: boolean }> {
  const response = await fetch(`${getApiBaseUrl()}/streaming/${sessionId}/stop`, {
    method: 'POST',
  });
  return handleResponse<{ success: boolean; aborted: boolean }>(response);
}

export async function cancelRequest(sessionId: string): Promise<{ success: boolean; cancelled: boolean }> {
  const url = `${getApiBaseUrl()}/streaming/${sessionId}/cancel`;
  const response = await fetch(url, {
    method: 'POST',
  });
  return handleResponse<{ success: boolean; cancelled: boolean }>(response);
}

export async function createSessionFromContext(
  sessionId: string,
  payload: CreateSessionFromContextPayload,
): Promise<Session> {
  const response = await fetch(
    `${getApiBaseUrl()}/sessions/${sessionId}/create-from-context`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return handleResponse<Session>(response);
}

export async function getActiveSession(): Promise<Session | null> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/active`);
  if (response.status === 204) {
    return null;
  }
  return handleResponse<Session>(response);
}

export async function getSessions(filter?: string): Promise<Session[]> {
  const url = new URL(`${getApiBaseUrl()}/sessions`);
  if (filter) {
    url.searchParams.append('filter', filter);
  }
  const response = await fetch(url.toString());
  return handleResponse<Session[]>(response);
}

export async function getSessionDetails(sessionId: string, includeLogs = false): Promise<Session> {
  const url = new URL(`${getApiBaseUrl()}/sessions/${sessionId}`);
  if (includeLogs) {
    url.searchParams.append('includeLogs', 'true');
  }
  const response = await fetch(url.toString(), {
    headers: {
      'Cache-Control': 'no-cache', // Bypass ETag cache for fresh data
    },
  });
  return handleResponse<Session>(response);
}

export async function getChildSessions(parentSessionId: string): Promise<Session[]> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${parentSessionId}/child-sessions`);
  return handleResponse<Session[]>(response);
}

export interface SessionFileChange {
  file_path: string;
  initial_content: string | null;
  current_content: string | null;
}

export async function getSessionFileChanges(sessionId: string): Promise<SessionFileChange[]> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}/file-changes`);
  return handleResponse<SessionFileChange[]>(response);
}

export async function updateSession(
  sessionId: string,
  payload: UpdateSessionPayload,
): Promise<Session> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  return handleResponse<Session>(response);
}

export async function deleteSession(sessionId: string): Promise<void> {
    const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}`, {
        method: 'DELETE',
    });
    await handleResponse<void>(response);
}

export async function cloneSession(sessionId: string): Promise<Session> {
    const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}/clone`, {
        method: 'POST',
    });
    return handleResponse<Session>(response);
}

export async function exportSession(sessionId: string): Promise<void> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}/export`);
  if (!response.ok) {
    await handleResponse(response); // Will throw an error
    return;
  }
  
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  
  const contentDisposition = response.headers.get('content-disposition');
  let filename = `session-export-${sessionId}.json`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    if (filenameMatch && filenameMatch.length > 1) {
      filename = filenameMatch[1];
    }
  }
  
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function importSession(file: File): Promise<ImportSessionResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${getApiBaseUrl()}/sessions/import`, {
    method: 'POST',
    body: formData,
  });
  return handleResponse<ImportSessionResponse>(response);
}


export interface SubmitPromptPayload {
  user_prompt: string;
  context_template_id?: string | null;
  ad_hoc_context_definition?: string | null;
  execution_strategy: string; // 'review_first' or 'apply_revert'
}

export async function submitPrompt(
  sessionId: string,
  promptData: SubmitPromptPayload,
): Promise<SessionInput> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}/inputs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(promptData),
  });
  return handleResponse<SessionInput>(response);
}

export async function deleteSessionInput(sessionId: string, inputId: string): Promise<void> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}/inputs/${inputId}`, {
      method: 'DELETE',
  });
  await handleResponse<void>(response);
}

export async function deleteSessionInputUntilHere(sessionId: string, inputId: string): Promise<void> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}/inputs/${inputId}/until-here`, {
      method: 'DELETE',
  });
  await handleResponse<void>(response);
}

export async function retrySessionInput(
  sessionId: string,
  inputId: string,
  promptData: { user_prompt: string; execution_strategy?: string },
): Promise<SessionInput> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}/inputs/${inputId}/retry`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(promptData),
  });
  return handleResponse<SessionInput>(response);
}

export async function regenerateAiResponse(
  sessionId: string,
  inputId: string,
): Promise<SessionInput> {
  const response = await fetch(
    `${getApiBaseUrl()}/sessions/${sessionId}/inputs/${inputId}/regenerate`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return handleResponse<SessionInput>(response);
}

export interface RevertSessionFileResponse {
  success: boolean;
  file_path: string;
}

export async function revertSessionFile(
  sessionId: string,
  filePath: string,
): Promise<RevertSessionFileResponse> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}/revert-file`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ file_path: filePath }),
  });
  return handleResponse<RevertSessionFileResponse>(response);
}

export interface RevertSessionResponse {
  reverted: string[];
  failed: string[];
  errors: string[];
}

export async function revertSession(sessionId: string): Promise<RevertSessionResponse> {
  const response = await fetch(`${getApiBaseUrl()}/sessions/${sessionId}/revert-session`, {
    method: 'POST',
  });
  return handleResponse<RevertSessionResponse>(response);
}

export async function getInputFileChanges(
  sessionId: string,
  inputIds: string[],
): Promise<SessionFileChange[]> {
  const response = await fetch(
    `${getApiBaseUrl()}/sessions/${sessionId}/input-file-changes?inputIds=${inputIds.join(',')}`,
  );
  return handleResponse<SessionFileChange[]>(response);
}

export async function revertInputFileChanges(
  sessionId: string,
  inputIds: string[],
): Promise<RevertSessionResponse> {
  const response = await fetch(
    `${getApiBaseUrl()}/sessions/${sessionId}/revert-input-file-changes`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ input_ids: inputIds }),
    },
  );
  return handleResponse<RevertSessionResponse>(response);
}

/**
 * Lazy-load a single input's AIActions with their executionLogs.
 * Use this instead of includeLogs=true on the session endpoint.
 */
export async function getInputWithLogs(
  sessionId: string,
  inputId: string,
): Promise<SessionInput> {
  const response = await fetch(
    `${getApiBaseUrl()}/sessions/${sessionId}/inputs/${inputId}/actions-with-logs`,
    {
      headers: {
        'Cache-Control': 'no-cache',
      },
    },
  );
  return handleResponse<SessionInput>(response);
}

export interface BatchToggleDiscardedPayload {
  input_ids: string[];
  is_discarded: boolean;
}

export interface BatchToggleDiscardedResponse {
  affected: number;
}

export async function toggleDiscardedBatch(
  sessionId: string,
  payload: BatchToggleDiscardedPayload,
): Promise<BatchToggleDiscardedResponse> {
  const response = await fetch(
    `${getApiBaseUrl()}/sessions/${sessionId}/inputs/batch-toggle-discarded`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return handleResponse<BatchToggleDiscardedResponse>(response);
}

export async function restoreSummary(
  sessionId: string,
  summaryInputId: string,
): Promise<{ success: boolean }> {
  const response = await fetch(
    `${getApiBaseUrl()}/sessions/${sessionId}/inputs/${summaryInputId}/restore-summary`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return handleResponse<{ success: boolean }>(response);
}